/**
 * Gestionnaires de tournois du club (squash_manger_api_v2/docs/TOURNAMENT_MANAGERS.md) : un joueur sans rôle dans le club
 * reçoit des droits sur CERTAINS tournois seulement (tournois créés dans l'app ou importés de Tournament Planner). Fonctions
 * pures (pas de React, pas d'API) partagées par l'AuthContext, les gardes de route, le menu et l'écran Matchs.
 *
 * Gardes d'interface seulement : l'API fait foi (403 `tournament_forbidden`).
 */

/** Accueil et zone de l'ERP d'un staff de tournoi officiel : « Mes tournois officiels ». */
export const OFFICIAL_TOURNAMENTS_PATH = '/app/tournois-officiels';

/** Accueil d'un gestionnaire de tournois du club : l'écran Matchs, onglet Tournoi. */
export const MANAGER_HOME_PATH = '/app/matches?vue=tournoi';

/** Droits d'un membre d'équipe, dans l'ordre des bits de l'API (`TournamentPermission`). */
export const MANAGER_PERMISSIONS = ['schedule', 'courts', 'checkin', 'referee', 'results', 'draw', 'manage_staff'];

/**
 * Tournois gérés, au format mémorisé, depuis `managed_tournaments` de manager-login ou `tournaments` de
 * GET /TournamentStaff/my : `{ id, tournament_id?, match_import_id?, name, role, permissions }`. `id` = id du lot de
 * l'écran Matchs (tournoi de l'app ou import) ; `kind` : « tournament » | « import ».
 */
export function normalizeManagedTournaments(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((m) => m && (m.id || m.tournament_id || m.match_import_id))
    .map((m) => {
      const tournamentId = m.tournament_id ? String(m.tournament_id) : null;
      const importId = m.match_import_id ? String(m.match_import_id) : null;
      return {
        id: String(m.id ?? tournamentId ?? importId),
        tournament_id: tournamentId,
        match_import_id: importId,
        kind: importId && !tournamentId ? 'import' : 'tournament',
        name: m.name || 'Tournoi',
        role: m.role || 'adjoint',
        permissions: Array.isArray(m.permissions) ? m.permissions.map(String) : [],
      };
    });
}

/** Tournois du club gérés par l'utilisateur mémorisé (vide pour un admin du club ou un staff officiel seul). */
export function managedTournamentsOf(user) {
  return Array.isArray(user?.managedTournaments) ? user.managedTournaments : [];
}

/** Vrai pour un gestionnaire (vue limitée de l'ERP) ayant au moins un tournoi du club. */
export function isTournamentManager(user) {
  return user?.role === 'tournament_staff' && managedTournamentsOf(user).length > 0;
}

/** Tournoi géré dont l'id (lot de l'écran Matchs, `tournament_id` ou `match_import_id`) vaut `id`, sinon null. */
export function managedTournament(user, id) {
  if (id == null || id === '') return null;
  const key = String(id);
  return managedTournamentsOf(user).find((m) => m.id === key || m.tournament_id === key || m.match_import_id === key) || null;
}

/**
 * Droits de l'utilisateur sur un tournoi : `null` = tous (admin du club), sinon la liste de ses permissions (vide = aucun
 * droit, tournoi qui n'est pas à lui).
 */
export function tournamentPermissionsFor(user, id) {
  if (user?.role === 'admin') return null;
  return managedTournament(user, id)?.permissions || [];
}

/** Vrai si l'utilisateur a TOUTES les permissions `perms` sur le tournoi `id` (admin du club : toujours). */
export function canOnTournament(user, id, ...perms) {
  const granted = tournamentPermissionsFor(user, id);
  if (granted === null) return true;
  return perms.length > 0 && perms.every((p) => granted.includes(p));
}

/** Accueil d'un staff de tournoi : l'écran Matchs s'il gère des tournois du club, sinon « Mes tournois officiels ». */
export function tournamentStaffHomePath(user) {
  return managedTournamentsOf(user).length > 0 ? MANAGER_HOME_PATH : OFFICIAL_TOURNAMENTS_PATH;
}

/** Chemin sans barre finale ni requête. */
function cleanPath(pathname) {
  return String(pathname || '').split('?')[0].replace(/\/+$/, '');
}

/** `/base` ou `/base/:id` (un seul segment après), avec l'id. */
function matchSingle(path, base) {
  if (path === base) return { exact: true, id: null };
  if (!path.startsWith(`${base}/`)) return null;
  const rest = path.slice(base.length + 1);
  return rest && !rest.includes('/') ? { exact: false, id: decodeURIComponent(rest) } : null;
}

/**
 * Pages de l'ERP permises à un staff de tournoi (`role === 'tournament_staff'`) :
 * - toujours « Mes tournois officiels » et la fiche d'un tournoi officiel (l'API filtre) ;
 * - gestionnaire de tournois du club : l'écran Matchs, le bracket d'un de ses tournois et la fiche d'un de ses tournois
 *   de l'app.
 */
export function tournamentStaffPathAllowed(pathname, user) {
  const path = cleanPath(pathname);
  if (matchSingle(path, OFFICIAL_TOURNAMENTS_PATH)) return true;
  if (managedTournamentsOf(user).length === 0) return false;
  if (path === '/app/matches') return true;
  const bracket = matchSingle(path, '/app/matches/bracket');
  if (bracket && !bracket.exact) return Boolean(managedTournament(user, bracket.id));
  const detail = matchSingle(path, '/app/tournaments');
  if (detail && !detail.exact) return Boolean(managedTournament(user, detail.id)?.tournament_id);
  return false;
}

/**
 * Actions d'une ligne de l'écran Matchs selon les droits sur son tournoi (MatchController / ClubMatchController) :
 * lancer, arrêter / valider, arbitrer, terrain et arbitre d'un match en cours, présence, incidents = `referee` ;
 * saisie du score = `results` ; modifier l'horaire (fiche d'un match importé) = `schedule`. `null` = tout permis.
 */
export function matchRowCapabilities(permissions) {
  if (permissions === null || permissions === undefined) return null;
  const has = (p) => permissions.includes(p);
  return {
    referee: has('referee'),
    results: has('results'),
    schedule: has('schedule'),
    any: permissions.length > 0,
  };
}

/** Infobulle d'une action refusée faute du droit `perm`. */
export const MISSING_PERMISSION_HINT = {
  referee: 'Droit « Arbitrage » requis sur ce tournoi',
  results: 'Droit « Scores » requis sur ce tournoi',
  schedule: 'Droit « Horaires » requis sur ce tournoi',
};
